import { Button, Col, Form, Input, Modal, Row } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect, useRef } from 'react';
import { isNumericInt } from '../../Utils/common';

const EditProductModal = ({ onOpen, onCancel, item, onCallback, labelLanguage }: any) => {
  const [form] = Form.useForm();
  const defaultLabelRef = useRef<any>(null);

  const renderLabel = (th: string, en: string) => (
    <span>
      <span
        style={{
          color: labelLanguage === 'th' ? '#00B3F0' : '#666',
          textDecoration: labelLanguage === 'th' ? 'underline' : 'none',
          fontWeight: labelLanguage === 'th' ? 600 : 400,
        }}
      >
        {th}
      </span>
      <span style={{ color: '#999', margin: '0 4px' }}>/</span>
      <span
        style={{
          color: labelLanguage === 'en' ? '#00B3F0' : '#666',
          textDecoration: labelLanguage === 'en' ? 'underline' : 'none',
          fontWeight: labelLanguage === 'en' ? 600 : 400,
        }}
      >
        {en}
      </span>
    </span>
  );

  const getDetailText = () => {
    if (!item) return '';

    return (
      '- [1 ชิ้น = ' +
      item.item_price +
      ' บาท]' +
      (1 === item.item_sale_pack
        ? ''
        : ', [' + item.item_sale_pack + ' ชิ้น = ' + item.item_sale_pack_price + ' บาท]') +
      (1 === item.item_pack ? '' : ', [' + item.item_pack + ' ชิ้น = ' + item.item_pack_price + ' บาท]') +
      (item.item_pack === item.item_big_pack && item.item_pack_price === item.item_big_pack_price
        ? ''
        : ', [' + item.item_big_pack + ' ชิ้น = ' + item.item_big_pack_price + ' บาท]') +
      '\n' +
      (item.item_max === 0 ? '- ไม่จำกัดจำนวนการซื้อ' : '- จำกัดการขาย ' + item.item_max + ' ชิ้น ต่อ 1 ใบเสร็จ')
    );
  };

  useEffect(() => {
    if (item) {
      defaultLabelRef.current = {
        item_component: item.item_component ?? '',
        item_memo: item.item_memo ?? '',
        item_method: item.item_method ?? '',
        remark: item.remark ?? '',
        item_warning: item.item_warning ?? '',
      };
    }
  }, [item, labelLanguage]);

  const getLabelValue = (field: string) => {
    if (!item) return '';

    const langValue = item?.label_by_lang?.[labelLanguage]?.[field];

    if (typeof langValue === 'string' && langValue.trim() !== '') {
      return langValue;
    }

    if (labelLanguage === 'en') {
      return field === 'item_component' ? defaultLabelRef.current?.item_component ?? '' : '';
    }

    return defaultLabelRef.current?.[field] ?? '';
  };

  const setFormFieldsByLanguage = () => {
    if (!item) return;

    form.setFieldsValue({
      item_name: item.item_name ?? '',
      item_size: item.item_size ?? '',
      detail: getDetailText(),
      item_qty: item.item_qty ?? 1,
      item_total_price: item.item_total_price ?? item.item_qty * item.item_price,
      item_component: getLabelValue('item_component'),
      item_memo: getLabelValue('item_memo'),
      item_method: getLabelValue('item_method'),
      remark: getLabelValue('remark'),
      item_warning: getLabelValue('item_warning'),
    });
  };

  useEffect(() => {
    if (onOpen && item) {
      setFormFieldsByLanguage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelLanguage, onOpen, item]);

  const calculateTotalPrice = () => {
    let qty = Number(form.getFieldValue('item_qty')) || 0;
    if (!Number.isFinite(qty) || qty <= 0) return 0;

    // Build candidate packs
    const packs = [
      { name: 'sale', size: item?.item_sale_pack, price: item?.item_sale_pack_price },
      { name: 'big', size: item?.item_big_pack, price: item?.item_big_pack_price },
      { name: 'small', size: item?.item_pack, price: item?.item_pack_price },
      { name: 'unit', size: 1, price: item?.item_price },
    ]
      .filter(
        (p) =>
          Number.isFinite(p.size) &&
          Number.isFinite(p.price) &&
          p.size > 0 &&
          Number.isInteger(p.size as number) &&
          p.price >= 0,
      )
      .sort((a, b) => b.size - a.size || a.price - b.price);

    if (packs.length === 0) {
      return 0;
    }

    let total = 0;
    const breakdown: { name: string; count: number; size: number; price: number }[] = [];

    let remaining = qty;
    for (const p of packs) {
      if (remaining <= 0) break;
      const count = Math.floor(remaining / p.size);
      if (count > 0) {
        total += count * p.price;
        remaining -= count * p.size;
        breakdown.push({ name: p.name, count, size: p.size, price: p.price });
      }
    }

    form.setFieldsValue({
      item_total_price: total,
    });
  };

  const handleQtyChange = (e: any) => {
    calculateTotalPrice();
  };

  const requiredTrimRule = (message: string) => ({
    validator: (_: any, value: any) => {
      if (value?.toString().trim()) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(message));
    },
  });

  const handleSubmit = async () => {
    if (!item) return;

    try {
      const formValues = await form.validateFields();

      const nextLangData = {
        item_component: formValues.item_component?.trim?.() ?? '',
        item_memo: formValues.item_memo?.trim?.() ?? '',
        item_method: formValues.item_method?.trim?.() ?? '',
        remark: formValues.remark ?? '',
        item_warning: formValues.item_warning ?? '',
      };

      const baseTh = {
        item_component:
          item.label_by_lang?.th?.item_component ||
          defaultLabelRef.current?.item_component ||
          item.item_component ||
          '',
        item_memo: item.label_by_lang?.th?.item_memo || defaultLabelRef.current?.item_memo || item.item_memo || '',
        item_method:
          item.label_by_lang?.th?.item_method || defaultLabelRef.current?.item_method || item.item_method || '',
        remark: item.label_by_lang?.th?.remark || defaultLabelRef.current?.remark || item.remark || '',
        item_warning:
          item.label_by_lang?.th?.item_warning || defaultLabelRef.current?.item_warning || item.item_warning || '',
      };

      const baseEn = {
        item_component: item.label_by_lang?.en?.item_component ?? '',
        item_memo: item.label_by_lang?.en?.item_memo ?? '',
        item_method: item.label_by_lang?.en?.item_method ?? '',
        remark: item.label_by_lang?.en?.remark ?? '',
        item_warning: item.label_by_lang?.en?.item_warning ?? '',
      };

      const updatedLabelByLang = {
        th: labelLanguage === 'th' ? nextLangData : baseTh,
        en: labelLanguage === 'en' ? nextLangData : baseEn,
      };

      const data = {
        ...item,
        key: item.item_code,
        item_qty: Number(formValues.item_qty) || 0,
        item_total_price: Number(formValues.item_total_price) || 0,
        item_component: baseTh.item_component,
        item_memo: baseTh.item_memo,
        item_method: baseTh.item_method,
        remark: baseTh.remark,
        item_warning: baseTh.item_warning,
        label_by_lang: updatedLabelByLang,
      };

      onCallback(data);
      onCancel(false);
    } catch (error) {
      return;
    }
  };

  const handleClose = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    onCancel();
  };

  return (
    <div>
      <Modal
        title="แก้ไขรายละเอียดการเพิ่มสินค้า"
        centered
        open={onOpen}
        onCancel={handleClose}
        destroyOnClose
        footer={[
          <Button key="save" type="primary" onClick={handleSubmit}>
            บันทึก
          </Button>,
          <Button key="back" type="default" onClick={handleClose}>
            ปิด
          </Button>,
        ]}
        width={800}
      >
        <Row style={{ marginBottom: 12 }}>
          <Col span={24}>
            <div style={{ fontSize: 14 }}>
              <span
                style={{
                  color: labelLanguage === 'th' ? '#00B3F0' : '#666',
                  textDecoration: labelLanguage === 'th' ? 'underline' : 'none',
                  fontWeight: labelLanguage === 'th' ? 600 : 400,
                  marginRight: 8,
                }}
              >
                Thai
              </span>
              <span style={{ color: '#999', marginRight: 8 }}>/</span>
              <span
                style={{
                  color: labelLanguage === 'en' ? '#00B3F0' : '#666',
                  textDecoration: labelLanguage === 'en' ? 'underline' : 'none',
                  fontWeight: labelLanguage === 'en' ? 600 : 400,
                }}
              >
                English
              </span>
            </div>
          </Col>
        </Row>

        <Form layout="vertical" form={form}>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={renderLabel('ชื่อยา', 'Drug Name')} name="item_name">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={renderLabel('ขนาด', 'Size')} name="item_size">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label={renderLabel('รายละเอียดการขาย', 'Sales Details')} name="detail">
                <TextArea style={{ resize: 'none' }} disabled rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={renderLabel('จำนวน', 'Quantity')} name="item_qty">
                <Input
                  style={{ textAlign: 'end' }}
                  addonAfter="ชิ้น"
                  onChange={handleQtyChange}
                  onKeyPress={(event) => {
                    isNumericInt(event);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={renderLabel('ราคารวม', 'Total Price')} name="item_total_price">
                <Input style={{ textAlign: 'end' }} disabled addonAfter="บาท" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={renderLabel('ส่วนประกอบ', 'Active Ingredient')}
                name="item_component"
                rules={item?.is_drug ? [requiredTrimRule('กรุณากรอกส่วนประกอบ')] : []}
              >
                <TextArea style={{ resize: 'none' }} rows={3} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={renderLabel('ข้อบ่งใช้', 'Indications')}
                name="item_memo"
                rules={[requiredTrimRule('กรุณากรอกข้อบ่งใช้')]}
              >
                <TextArea style={{ resize: 'none' }} rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label={renderLabel('วิธีใช้', 'Dosage and Administration')}
                name="item_method"
                rules={[requiredTrimRule('กรุณากรอกวิธีใช้')]}
              >
                <TextArea style={{ resize: 'none' }} rows={3} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label={renderLabel('คำแนะนำ', 'Special Instructions')} name="remark">
                <TextArea style={{ resize: 'none' }} rows={3} maxLength={200} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label={renderLabel('ข้อควรระวัง', 'Precautions')} name="item_warning">
                <TextArea style={{ resize: 'none' }} rows={3} maxLength={200} showCount />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default EditProductModal;
